import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { getSessionStatus, toggleSession } from "../../api/sessionApi";
import "../../styles/SessionPage.css";

const SessionPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [lastToggled, setLastToggled] = useState(null);

  // Fetch current session status on mount
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await getSessionStatus();
      setIsOpen(data.is_open);
      setLastToggled(data.last_toggled || null);
    } catch (err) {
      console.error("Failed to fetch session status:", err);
      Swal.fire({
        title: "Error",
        text: "Unable to fetch session status. Check your backend connection.",
        icon: "error",
        confirmButtonColor: "#a92f6f",
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle open/close session with confirmation popup
  const handleToggle = async () => {
    const action = isOpen ? "close" : "open";
    const actionColor = isOpen ? "#ef4444" : "#22c55e";
    
    const confirm = await Swal.fire({
      title: `Confirm ${action} session?`,
      html: `
        <div class="session-confirm">
          <div class="confirm-icon" style="color: ${actionColor}">
            ${isOpen ? "🔒" : "🔓"}
          </div>
          <p>You are about to <strong>${action}</strong> the session.</p>
          ${isOpen 
            ? "<p class='confirm-warning'>⚠️ Workers will no longer be able to capture data.</p>" 
            : "<p class='confirm-info'>✅ Workers will be able to start capturing data.</p>"
          }
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action} it`,
      cancelButtonText: "Cancel",
      confirmButtonColor: isOpen ? "#ef4444" : "#22c55e",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
      customClass: {
        popup: "session-swal-popup",
        title: "session-swal-title",
      },
    });

    if (!confirm.isConfirmed) return;

    try {
      setToggling(true);
      const data = await toggleSession(!isOpen);
      setIsOpen(data.is_open);
      setLastToggled(new Date().toISOString());
      
      Swal.fire({
        title: "Success!",
        text: `Session has been ${action}ed successfully.`,
        icon: "success",
        confirmButtonColor: "#a92f6f",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error("Toggle failed:", err);
      Swal.fire({
        title: "Error",
        text: "Unable to toggle session. Check backend or permissions.",
        icon: "error",
        confirmButtonColor: "#a92f6f",
      });
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Auto-refresh status every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Format last toggled time
  const getLastToggledText = () => {
    if (!lastToggled) return "Never";
    const date = new Date(lastToggled);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="session-container">
        <div className="session-card loading">
          <div className="session-loader">
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
          </div>
          <p className="loader-text">Checking session status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-container">
      <div className={`session-card ${isOpen ? 'open' : 'closed'}`}>
        {/* Decorative accent bar */}
        <div className="card-accent"></div>

        {/* Header */}
        <div className="session-header">
          <h2>Packhouse Session Control</h2>
          <div className={`status-pill ${isOpen ? 'open' : 'closed'}`}>
            <span className="status-dot"></span>
            {isOpen ? "Open" : "Closed"}
          </div>
        </div>

        {/* Main Status Display */}
        <div className="status-display">
          <div className={`status-icon ${isOpen ? 'open' : 'closed'}`}>
            {isOpen ? "🔓" : "🔒"}
          </div>
          <h3 className="status-title">
            {isOpen ? "Session is Active" : "Session is Inactive"}
          </h3>
          <p className="status-description">
            {isOpen 
              ? "Workers can currently capture harvest data. Close the session to stop data entry."
              : "No data can be captured at this time. Open the session to allow workers to begin."
            }
          </p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Last Changed</span>
            <span className="stat-value">{getLastToggledText()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Current Status</span>
            <span className={`stat-value ${isOpen ? 'open' : 'closed'}`}>
              {isOpen ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleToggle}
          disabled={toggling}
          className={`session-toggle-btn ${isOpen ? 'close' : 'open'} ${toggling ? 'toggling' : ''}`}
        >
          {toggling ? (
            <>
              <span className="btn-spinner"></span>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span className="btn-icon">{isOpen ? "🔒" : "🔓"}</span>
              <span>{isOpen ? "Close Session" : "Open Session"}</span>
            </>
          )}
        </button>

        {/* Info Footer */}
        <div className="session-footer">
          <div className="footer-icon">ℹ️</div>
          <p>
            {isOpen 
              ? "Closing the session will prevent all workers from submitting new captures."
              : "Opening the session will allow workers to start capturing harvest data."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionPage;